import { Action } from 'redux';

import { JsonApiResponse, JsonApiRelationships } from '../../shared/utilities/jsonApiNormalizer';

import { APICallStatus } from '../../design-system/types';
import { Voucher, DeferAgreement, DossierId } from '../dossier/types';

import * as actions from './actions';

export type ContractId = DossierId;

export type Contract = {
    validatedAt: Date | null;
    price: Price | null;
    legalInformation: LegalInformationFromContract;
    agencyParty: AgencyParty;
    proposal: Proposal | null;
    vouchers: Array<Voucher>;
    paymentSchedule: ExistingPaymentSchedule | null;
    deferAgreement: DeferAgreement | null;
    travelersInformation: Travelers;
    currencies: Currencies;
    travelDates: {
        arrivalDate: Date;
        departureDate: Date;
    };
};

export type Currencies = {
    [id: string]: Currency;
};

export type Traveler = {
    firstName: string;
    lastName: string;
    country: string;
    birthDate: string;
};

type Travelers = Array<Traveler>;

export type LegalInformationFromContract = {
    url: string;
    reference: string;
    version: string;
};

export type AgencyParty = {
    name: string;
    contactFirstName: string;
    contactLastName: string;
    email: string;
    address: string;
    phone: string;
};

export type Proposal = {
    url: string;
    date: Date;
};

export type Instalment = {
    dueDate: string;
    price: Price;
    type: string;
    payerName: string;
    label: string;
};

export type InstalmentViewModel = {
    id: string;
    dueDate: Date;
    price: Price;
    type: string;
    payerName: string;
    label: string;
    status: string;
    paidAt: Date | null;
};

export type InstalmentBody = {
    due_date: string;
    price: Price;
    type: string;
    payer_name: string;
    label: string;
};

export interface ExistingInstalment extends Instalment {
    id: string;
    status: string;
    paidAt: string | null;
}

export interface PaymentSchedule {
    currency: string;
    instalments: Instalment[];
}

export interface PaymentScheduleBody {
    currency: string;
    instalments: InstalmentBody[];
}

export interface ExistingPaymentSchedule extends PaymentSchedule {
    instalments: ExistingInstalment[];
    token: string;
}

export interface PaymentScheduleViewModel {
    currency?: string;
    depositInstalments: InstalmentViewModel[];
    balanceInstalments: InstalmentViewModel[];
    token?: string;
}

export type Price = {
    amount: string | number;
    currency: string;
};

export interface SerializedLegalInformation {
    id: string;
    reference: string;
    language: string;
    destination: string;
}

export type LegalInformation = {
    reference: string;
};

export type InstalmentTypes = 'deposit' | 'balance';

export type Currency = {
    subunit: number;
    currency: string;
};

export enum ContractEditStep {
    TRAVEL_INFORMATION = 1,
    TRAVELERS,
    PRICING,
    PAYMENT_SCHEDULE,
}

export type TravelInformation = {
    legalInformation?: LegalInformationFromContract | LegalInformation;
    arrivalDate: Date;
    departureDate: Date;
    proposal?: string | File | URL;
    legalInformationReference?: string;
};

export type ContractState = {
    entity: Contract | null;
    error?: string;
    currentStep: ContractEditStep;
    shouldRecreatePaymentSchedule: boolean;
    previousPaymentSchedule?: PaymentScheduleViewModel;
    nextCurrency?: string;
};

export type LegalInformationState = {
    entity: SerializedLegalInformation[];
    error?: string;
};

// Action Types
export interface FetchContractRequestAction extends Action<
    typeof actions.fetchContractRequestAction
> {
    contractId: ContractId;
}

export interface FetchContractSuccessAction extends Action<
    typeof actions.fetchContractSuccessAction
> {
    contractId: ContractId;
    response: Contract | null;
}

export interface FetchContractFailureAction extends Action<
    typeof actions.fetchContractFailureAction
> {
    contractId: ContractId;
    error: string;
}

export interface PostContractRequestAction extends Action<
    typeof actions.postContractRequestAction
> {
    contractId: ContractId;
    travelInformation: TravelInformation;
    travelers: Traveler[];
}

export interface PostContractSuccessAction extends Action<
    typeof actions.postContractSuccessAction
> {
    contractId: ContractId;
    response: Contract;
}

export interface PostContractFailureAction extends Action<
    typeof actions.postContractFailureAction
> {
    contractId: ContractId;
    error: string;
}

export interface FetchLegalInformationRequestAction extends Action<
    typeof actions.fetchLegalInformationRequestAction
> {
    contractId: ContractId;
}

export interface FetchLegalInformationSuccessAction extends Action<
    typeof actions.fetchLegalInformationSuccessAction
> {
    contractId: ContractId;
    response: SerializedLegalInformation[];
}

export interface FetchLegalInformationFailureAction extends Action<
    typeof actions.fetchLegalInformationFailureAction
> {
    contractId: ContractId;
    error: string;
}

export interface SetContractEditStepAction extends Action<
    typeof actions.setContractEditStepAction
> {
    step: ContractEditStep;
}

export interface FetchQuotePricingRequestAction extends Action<
    typeof actions.fetchQuotePricingRequestAction
> {
    contractId: ContractId;
}

export interface FetchQuotePricingSuccessAction extends Action<
    typeof actions.fetchQuotePricingSuccessAction
> {
    contractId: ContractId;
    response: QuotePricing | null;
}

export interface FetchQuotePricingFailureAction extends Action<
    typeof actions.fetchQuotePricingFailureAction
> {
    contractId: ContractId;
    error: string;
}

export interface PostProofOfDocumentsRequestAction extends Action<
    typeof actions.postProofOfDocumentsRequestAction
> {
    contractId: ContractId;
    file: File;
}

export interface PostProofOfDocumentsSuccessAction extends Action<
    typeof actions.postProofOfDocumentsSuccessAction
> {
    response: ProofDocument;
    fileName: string;
}

export interface PostProofOfDocumentsFailureAction extends Action<
    typeof actions.postProofOfDocumentsFailureAction
> {
    error: string;
    file: File;
}

export interface DeleteProofOfDocumentsRequestAction extends Action<
    typeof actions.deleteProofOfDocumentRequestAction
> {
    fileName: string;
}

export interface DeleteProofOfDocumentsSuccessAction extends Action<
    typeof actions.deleteProofOfDocumentsSuccessAction
> {
    response: ProofDocument;
    fileName: string;
}

export interface DeleteProofOfDocumentsFailureAction extends Action<
    typeof actions.deleteProofOfDocumentsFailureAction
> {
    error: string;
}

export interface PostChangeQuotePricesRequestAction extends Action<
    typeof actions.postChangeQuotePricesRequestAction
> {
    contractId: ContractId;
    quotePrices: FilledQuotePricing;
}

export interface PostChangeQuotePricesSuccessAction extends Action<
    typeof actions.postChangeQuotePricesSuccessAction
> {
    response: QuotePricing;
}

export interface PostChangeQuotePricesFailureAction extends Action<
    typeof actions.postChangeQuotePricesFailureAction
> {
    error: string;
}

export interface PostPaymentScheduleRequestAction extends Action<
    typeof actions.postPaymentScheduleRequestAction
> {
    contractId: ContractId;
    paymentSchedule: PaymentScheduleViewModel;
    onSuccess: () => void;
}

export interface PostPaymentScheduleSuccessAction extends Action<
    typeof actions.postPaymentScheduleSuccessAction
> {
    response: ExistingPaymentSchedule;
}

export interface PostPaymentScheduleFailureAction extends Action<
    typeof actions.postPaymentScheduleFailureAction
> {
    error: string;
}

export type PreviousContractEditStepAction = Action<
    typeof actions.goToPreviousContractEditStepAction
>;

export interface ChangeCurrencyPaymentScheduleAction extends Action<
    typeof actions.changeCurrencyPaymentScheduleAction
> {
    paymentSchedule: PaymentScheduleViewModel;
    currency: string;
}

export interface PutPaymentScheduleRequestAction extends Action<
    typeof actions.putPaymentScheduleRequestAction
> {
    contractId: ContractId;
    paymentSchedule: PaymentScheduleViewModel;
    onSuccess: () => void;
}

export interface PutPaymentScheduleSuccessAction extends Action<
    typeof actions.putPaymentScheduleSuccessAction
> {
    response: ExistingPaymentSchedule;
}

export interface PutPaymentScheduleFailureAction extends Action<
    typeof actions.putPaymentScheduleFailureAction
> {
    error: string;
}

export interface PostInstalmentRequestAction extends Action<
    typeof actions.postInstalmentRequestAction
> {
    contractId: ContractId;
    instalment: InstalmentViewModel;
}

export interface PostInstalmentSuccessAction extends Action<
    typeof actions.postInstalmentSuccessAction
> {
    response: ExistingPaymentSchedule;
}

export interface PostInstalmentFailureAction extends Action<
    typeof actions.postInstalmentFailureAction
> {
    error: string;
}

export interface PutInstalmentRequestAction extends Action<
    typeof actions.putInstalmentRequestAction
> {
    contractId: ContractId;
    instalment: InstalmentViewModel;
}

export interface PutInstalmentSuccessAction extends Action<
    typeof actions.putInstalmentSuccessAction
> {
    response: ExistingPaymentSchedule;
}

export interface PutInstalmentFailureAction extends Action<
    typeof actions.putInstalmentFailureAction
> {
    error: string;
}

export interface DeleteInstalmentRequestAction extends Action<
    typeof actions.deleteInstalmentRequestAction
> {
    contractId: ContractId;
    instalmentId: string;
}

export interface DeleteInstalmentSuccessAction extends Action<
    typeof actions.deleteInstalmentSuccessAction
> {
    response: ExistingPaymentSchedule;
}

export interface DeleteInstalmentFailureAction extends Action<
    typeof actions.deleteInstalmentFailureAction
> {
    error: string;
}

export type ContractAction =
    | FetchContractSuccessAction
    | FetchContractFailureAction
    | FetchContractRequestAction
    | PostContractRequestAction
    | PostContractFailureAction
    | PostContractSuccessAction
    | SetContractEditStepAction
    | PreviousContractEditStepAction
    | ChangeCurrencyPaymentScheduleAction;

export type LegalInformationActions =
    | FetchLegalInformationSuccessAction
    | FetchLegalInformationFailureAction
    | FetchLegalInformationRequestAction;

export type QuotePricingActions =
    | FetchQuotePricingSuccessAction
    | FetchQuotePricingFailureAction
    | FetchQuotePricingRequestAction
    | PostProofOfDocumentsFailureAction
    | PostProofOfDocumentsRequestAction
    | PostProofOfDocumentsSuccessAction
    | DeleteProofOfDocumentsFailureAction
    | DeleteProofOfDocumentsRequestAction
    | DeleteProofOfDocumentsSuccessAction
    | PostChangeQuotePricesFailureAction
    | PostChangeQuotePricesRequestAction
    | PostChangeQuotePricesSuccessAction;

export type PaymentScheduleActions =
    | PostPaymentScheduleFailureAction
    | PostPaymentScheduleRequestAction
    | PostPaymentScheduleSuccessAction
    | PutPaymentScheduleFailureAction
    | PutPaymentScheduleRequestAction
    | PutPaymentScheduleSuccessAction;

export type InstalmentActions =
    | PostInstalmentRequestAction
    | PostInstalmentSuccessAction
    | PostInstalmentFailureAction
    | PutInstalmentRequestAction
    | PutInstalmentSuccessAction
    | PutInstalmentFailureAction
    | DeleteInstalmentRequestAction
    | DeleteInstalmentSuccessAction
    | DeleteInstalmentFailureAction;

export interface ContractResponseFromAPI extends JsonApiResponse {
    data: {
        type: 'contract';
        id: string;
        meta: {
            currencies: Currencies;
        };
        attributes: {
            validatedAt: Date | null;
            price: Price | null;
            legalInformation: LegalInformationFromContract;
            agencyParty: AgencyParty;
            proposal: Proposal | null;
            vouchers: Array<Voucher>;
            paymentSchedule: ExistingPaymentSchedule | null;
            deferAgreement: DeferAgreement | null;
            travelersInformation: Travelers;
            travelDates: {
                arrivalDate: string;
                departureDate: string;
            };
        };
        relationships: JsonApiRelationships;
    };
}

export interface LegalInformationResponseFromAPI extends JsonApiResponse {
    data: Array<{
        type: 'legal_information';
        id: string;
        meta: {
            destination: string;
        };
        attributes: {
            reference: string;
            language: string;
        };
        relationships: JsonApiRelationships;
    }>;
}

export interface ProofDocumentFromAPI {
    data: [
        {
            id: string;
            name: string;
            path: string;
            type: string;
        },
    ];
}

export interface NormalizedContract {
    contract: {
        [id in DossierId]: {
            type: 'contract';
            id: string;
            meta: {
                currencies: Currencies;
            };
            attributes: {
                validatedAt: Date | null;
                price: Price | null;
                legalInformation: LegalInformationFromContract;
                agencyParty: AgencyParty;
                proposal: Proposal | null;
                vouchers: Array<Voucher>;
                paymentSchedule: ExistingPaymentSchedule | null;
                deferAgreement: DeferAgreement | null;
                travelersInformation: Travelers;
                travelDates: {
                    arrivalDate: string;
                    departureDate: string;
                };
            };
        };
    };
}

export type ContractStatusState = {
    get?: APICallStatus;
    post?: APICallStatus;
};

// eslint-disable-next-line sonarjs/redundant-type-aliases -- semantic domain type, intentionally kept
export type LegalInformationStatusState = ContractStatusState;

export type PostContractBody = {
    contract_id: string;
    proposal: string | File;
    travelers_information: Array<{
        first_name: string;
        last_name: string;
        birth_date: string;
        country: string;
    }>;
    travel_dates: {
        arrival_date: string;
        departure_date: string;
    };
    legal_information: {
        reference: string;
    };
};

//Quote Pricing
export interface QuotePricingResponseFromAPI {
    data: {
        id: number;
        localAgencyCurrency: string;
        prices: FeesFromAPI;
        pricesHistory: PriceHistory[];
        proofDocuments: ProofDocument[];
    };
}

export interface QuotePricing {
    id: number;
    fees: Fees;
    localAgencyCurrency: string;
    proofDocuments: ProofDocument[];
}

interface Prices {
    internationalFlightsPrice: number;
    domesticFlightsPrice: number;
    landServicesPrice: number;
}

export interface DisplayQuotePricing extends Prices {
    currency?: string;
    totalPrice: number;
    pricePerPax: number;
}

export interface FilledQuotePricing extends DisplayQuotePricing {
    currency: string;
}

export interface QuotePricingPayloadForAPI {
    localAgencyCurrency: string;
    localAgencyDomesticFlightsPrice: string;
    localAgencyInternationalFlightsPrice: string;
    localAgencyLandServicesPrice: string;
    proofDocuments: ProofDocument[];
    quoteId: number;
}

interface Fees {
    domesticFlights: FeesAmount;
    internationalFlights: FeesAmount;
    landServices: FeesAmount;
}

interface FeesFromAPI {
    'domestic-flights': FeesAmount;
    'international-flights': FeesAmount;
    'land-services': FeesAmount;
}

type Pricing = {
    amount: number;
    currency: string;
};

export interface FeesAmount {
    localAgency: Pricing;
}

interface PriceHistory {
    date: string;
    newPrices: Fees;
    oldPrices: Fees[];
}

export interface ProofDocument {
    name: string;
    url: string;
}

export type QuotePricingState = {
    entity: QuotePricing | null;
    uploadedNotAttachedProofOfDocuments: ProofDocument[];
    failedProofOfDocuments: File[];
    error?: string;
};

export type QuotePricingStatusState = {
    get?: APICallStatus;
    post?: APICallStatus;
    postProofDocuments?: PostProofOfDocuments;
};

export type PostProofOfDocuments = {
    [name: string]: APICallStatus;
};

export type PaymentScheduleStatusState = {
    put?: APICallStatus;
    post?: APICallStatus;
};
